/**
 * Authentication-time chooser for publicly released VoxPont engines + VP varieties.
 *
 * Domain engines come from VOXPONT_RELEASED_ENGINES.
 * VP110–VP430 labels and grouping come only from vpModelRegistry (single catalog).
 * The chooser does not navigate; react-hook-form owns the value in VoxPontAuthPage.
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  VOXPONT_RELEASED_ENGINES,
  experimentalModelIdFromEngineId,
} from 'features/voxpontEngine/voxpontEngineRegistry';
import {
  formatVpIncludes,
  formatVpSelectorLabel,
  getVpModelByEngineId,
  listVpModelsGrouped,
  resolveVpReadinessBadge,
  VP_MODEL_CATEGORY_LABELS,
} from 'features/voxpont12/paired/models/vpModelRegistry';

function domainEngineLabel(engine) {
  return `VoxPont ${engine.id} — ${engine.title}`;
}

function VoxPontEngineChooser({ value, onChange, disabled = false }) {
  const domainEngines = useMemo(
    () => VOXPONT_RELEASED_ENGINES.filter((engine) => !experimentalModelIdFromEngineId(engine.id)),
    []
  );
  const grouped = useMemo(() => listVpModelsGrouped({ enabledOnly: true }), []);
  const selectedVp = useMemo(() => getVpModelByEngineId(value), [value]);
  const includes = selectedVp ? formatVpIncludes(selectedVp) : null;

  return (
    <div className="vpauth-model-chooser">
      <select
        id="vpauth-engine"
        className="vpauth-input vpauth-engine-select"
        aria-label="VoxPont Model"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        <optgroup label="Flagship & domain">
          {domainEngines.map((engine) => (
            <option key={engine.id} value={engine.id}>
              {domainEngineLabel(engine)}
            </option>
          ))}
        </optgroup>
        {Object.entries(VP_MODEL_CATEGORY_LABELS).map(([categoryKey, label]) => (
          <optgroup key={categoryKey} label={label}>
            {(grouped[categoryKey] || []).map((manifest) => (
              <option key={manifest.modelId} value={manifest.engineId}>
                {formatVpSelectorLabel(manifest)} · {resolveVpReadinessBadge(manifest)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {selectedVp ? (
        <div className="vpauth-model-detail" data-testid="vpauth-model-detail">
          <p className="vpauth-model-description">{selectedVp.purpose}</p>
          {includes ? (
            <p className="vpauth-model-includes">Includes: {includes}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

VoxPontEngineChooser.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default VoxPontEngineChooser;
